import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { AdBanner, AdInContent } from '../components/AdUnit';
import { fetchAdSenseConfig, DEFAULT_ADSENSE_CONFIG, type AdSenseConfig } from '../lib/adsense-config';
import {
  fetchTaxonomy,
  fetchRepairCases,
  fetchRepairReferences,
  revisionsForFamily,
  findFamily,
  findManufacturer,
  REF_MEASUREMENT_TYPES,
  type KbTaxonomy,
  type KbBoardRevision,
} from '../lib/repair-kb-client';
import type { RepairCase, RepairReference, RefMedia } from '../lib/repair-kb-client';
import {
  ChevronRight, ChevronLeft, ChevronDown,
  Wrench, Cpu, Loader, FileText, Search, X,
} from '../lib/icons';

function boardSegment(b: KbBoardRevision) {
  return b.name.toLowerCase().replace(/\s+/g, '-');
}

// ── Per-reference photo lightbox ──────────────────────────────────────────────

function PhotoLightbox({
  media, initialIdx, title, onClose,
}: {
  media:      RefMedia[];
  initialIdx: number;
  title:      string;
  onClose:    () => void;
}) {
  const [idx, setIdx]   = useState(initialIdx);
  const touchStartX     = useRef<number | null>(null);
  const touchStartY     = useRef<number | null>(null);
  const photo           = media[idx];

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape')     onClose();
      if (e.key === 'ArrowRight') setIdx(i => Math.min(media.length - 1, i + 1));
      if (e.key === 'ArrowLeft')  setIdx(i => Math.max(0, i - 1));
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, media.length]);

  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={e => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
      }}
      onTouchEnd={e => {
        if (touchStartX.current === null || touchStartY.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        const dy = e.changedTouches[0].clientY - touchStartY.current;
        touchStartX.current = null;
        touchStartY.current = null;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
          if (dx < 0) setIdx(i => Math.min(media.length - 1, i + 1));
          else        setIdx(i => Math.max(0, i - 1));
        }
      }}
    >
      <button
        className="absolute top-4 right-4 text-gray-400 hover:text-white p-2 z-10"
        onClick={onClose}
        aria-label="Close"
      >
        <X className="h-6 w-6" />
      </button>
      {idx > 0 && (
        <button
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-3 bg-gray-900/80 rounded-xl z-10"
          onClick={e => { e.stopPropagation(); setIdx(i => i - 1); }}
          aria-label="Previous"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {idx < media.length - 1 && (
        <button
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-3 bg-gray-900/80 rounded-xl z-10"
          onClick={e => { e.stopPropagation(); setIdx(i => i + 1); }}
          aria-label="Next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}
      <div
        className="max-w-5xl w-full max-h-[95vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <img
          src={photo.public_url}
          alt={photo.caption || title}
          className="w-full object-contain rounded-xl bg-gray-900"
        />
        {photo.caption && (
          <p className="text-sm text-gray-400 text-center mt-3">{photo.caption}</p>
        )}
        {media.length > 1 && (
          <div className="flex gap-2 mt-4 justify-center flex-wrap">
            {media.map((m, i) => (
              <button
                key={m.id}
                onClick={() => setIdx(i)}
                className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                  i === idx ? 'border-[#00BFFF]' : 'border-gray-700 hover:border-gray-500'
                }`}
              >
                <img src={m.public_url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-600 text-center mt-2">{idx + 1} / {media.length}</p>
      </div>
    </div>
  );
}

// ── Sort header button ────────────────────────────────────────────────────────

function SortBtn({
  label, col, sortCol, sortDir, onSort,
}: {
  label:   string;
  col:     'title' | 'board';
  sortCol: 'title' | 'board';
  sortDir: 'asc' | 'desc';
  onSort:  (col: 'title' | 'board') => void;
}) {
  const active = sortCol === col;
  return (
    <button
      onClick={() => onSort(col)}
      className={`flex items-center gap-1 hover:text-[#E0E0E0] transition-colors ${active ? 'text-[#E0E0E0]' : ''}`}
    >
      {label}
      <span className={`text-[10px] ${active ? 'text-[#FF1744]' : 'text-gray-700'}`}>
        {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

// ── Smooth scroll to element, accounting for sticky header ────────────────────

function scrollToRef(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const header = document.querySelector('header');
  const offset = header ? header.getBoundingClientRect().height + 16 : 96;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: 'smooth' });
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function RepairDevicePage({
  deviceSlug: propDeviceSlug,
  initialBoardId,
}: {
  deviceSlug?:     string;
  initialBoardId?: string;
} = {}) {
  const params     = useParams<{ deviceSlug: string }>();
  const deviceSlug = propDeviceSlug ?? params.deviceSlug;

  const [taxonomy, setTaxonomy] = useState<KbTaxonomy>({
    manufacturers: [], device_families: [], models: [], board_revisions: [],
  });
  const [cases,    setCases]    = useState<RepairCase[]>([]);
  const [refs,     setRefs]     = useState<RepairReference[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Filters
  const [boardId,       setBoardId]       = useState<string>('all');
  const [refTypeFilter, setRefTypeFilter] = useState<string>('all');
  const [search,        setSearch]        = useState('');

  // Table sort
  const [sortCol, setSortCol] = useState<'title' | 'board'>('title');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Lightbox: which reference + which photo within it
  const [lightbox,     setLightbox]     = useState<{ refId: string; photoIdx: number } | null>(null);
  const [adConfig,     setAdConfig]     = useState<AdSenseConfig>(DEFAULT_ADSENSE_CONFIG);
  const [activeRefId,  setActiveRefId]  = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    fetchAdSenseConfig().then(setAdConfig).catch(() => {});
  }, []);

  useEffect(() => {
    if (!deviceSlug) return;
    Promise.all([fetchTaxonomy(), fetchRepairCases(false), fetchRepairReferences(false)])
      .then(([tax, c, r]) => {
        setTaxonomy(tax);
        const family = findFamily(tax, deviceSlug);
        if (!family) { setNotFound(true); setLoading(false); return; }
        setCases(c.filter(x => x.device_family_id === family.id));
        setRefs(r.filter(x => x.device_family_id === family.id));
        if (initialBoardId) setBoardId(initialBoardId);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [deviceSlug, initialBoardId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <Loader className="h-6 w-6 animate-spin text-gray-600" />
      </div>
    );
  }

  const family = deviceSlug ? findFamily(taxonomy, deviceSlug) : undefined;

  if (notFound || !family) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Device not found.</p>
          <Link to="/repair-database" className="text-[#FF1744] text-sm hover:underline">
            ← Repair Database
          </Link>
        </div>
      </div>
    );
  }

  const mfr           = findManufacturer(taxonomy, family.manufacturer_id);
  const allBoards     = revisionsForFamily(taxonomy, family.id);
  const selectedBoard = allBoards.find(b => b.id === boardId);

  const boardsWithContent = allBoards.filter(b =>
    cases.some(c => c.board_revision_id === b.id) ||
    refs.some(r => r.board_revision_id  === b.id)
  );

  // Board filter
  const filteredCases = boardId === 'all' ? cases : cases.filter(c => c.board_revision_id === boardId);
  const refsForBoard  = boardId === 'all' ? refs  : refs.filter(r => r.board_revision_id  === boardId);

  // Measurement type filter
  const filteredRefs = refTypeFilter === 'all'
    ? refsForBoard
    : refsForBoard.filter(r => r.measurement_type === refTypeFilter);

  const presentRefTypes = REF_MEASUREMENT_TYPES.filter(t =>
    refsForBoard.some(r => r.measurement_type === t.value)
  );

  // Local search
  const q = search.trim().toLowerCase();
  const searchedCases = q
    ? filteredCases.filter(c =>
        (c.title || '').toLowerCase().includes(q) ||
        c.symptoms_summary.some(s => s.toLowerCase().includes(q)) ||
        (allBoards.find(b => b.id === c.board_revision_id)?.name ?? '').toLowerCase().includes(q)
      )
    : filteredCases;

  const searchedRefs = q
    ? filteredRefs.filter(r =>
        r.title.toLowerCase().includes(q) ||
        (r.notes || '').toLowerCase().includes(q) ||
        (allBoards.find(b => b.id === r.board_revision_id)?.name ?? '').toLowerCase().includes(q)
      )
    : filteredRefs;

  // Sort repair table
  const sortedCases = [...searchedCases].sort((a, b) => {
    let cmp = 0;
    if (sortCol === 'title') {
      cmp = (a.title || '').localeCompare(b.title || '');
    } else {
      const aName = allBoards.find(brd => brd.id === a.board_revision_id)?.name ?? '';
      const bName = allBoards.find(brd => brd.id === b.board_revision_id)?.name ?? '';
      cmp = aName.localeCompare(bName);
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  function handleSort(col: 'title' | 'board') {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  const caseUrl = (c: RepairCase) => {
    if (c.board_revision_id) {
      const board = allBoards.find(b => b.id === c.board_revision_id);
      if (board) return `/repair-database/${family.slug}/${boardSegment(board)}/${c.slug}`;
    }
    return `/repair-database/${family.slug}/${c.slug}`;
  };

  const urlBoard  = initialBoardId ? allBoards.find(b => b.id === initialBoardId) : undefined;
  const canonical = urlBoard
    ? `https://gamefixsolutions.com/repair-database/${family.slug}/${boardSegment(urlBoard)}`
    : `https://gamefixsolutions.com/repair-database/${family.slug}`;

  const lightboxRef = lightbox ? searchedRefs.find(r => r.id === lightbox.refId) : null;

  return (
    <div className="bg-[#0D0D0D] min-h-screen text-[#E0E0E0]">
      <SEOHead
        title={`${family.name}${selectedBoard ? ` ${selectedBoard.name}` : ''} Repairs | GameFix Solutions`}
        description={`Board-level repair documentation for ${family.name}${mfr ? ` by ${mfr.name}` : ''}. Repair cases, known-good values, and reference material.`}
        canonical={canonical}
      />

      {lightboxRef && lightbox && (
        <PhotoLightbox
          media={lightboxRef.repair_reference_media ?? []}
          initialIdx={lightbox.photoIdx}
          title={lightboxRef.title}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Breadcrumb */}
      <div className="border-b border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
            <Link to="/" className="hover:text-gray-300">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/repair-database" className="hover:text-gray-300">Repair Database</Link>
            {mfr && (
              <><ChevronRight className="h-3 w-3" /><span className="text-gray-400">{mfr.name}</span></>
            )}
            <ChevronRight className="h-3 w-3" />
            {selectedBoard ? (
              <>
                <Link to={`/repair-database/${family.slug}`} className="hover:text-gray-300">{family.name}</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-[#E0E0E0] font-mono">{selectedBoard.name}</span>
              </>
            ) : (
              <span className="text-[#E0E0E0]">{family.name}</span>
            )}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="lg:flex lg:gap-8">

          {/* ── Left sidebar (desktop lg+) ── */}
          <aside className="hidden lg:block w-[220px] flex-shrink-0">
            <div
              className="sticky overflow-y-auto"
              style={{ top: '5.5rem', maxHeight: 'calc(100vh - 6rem)' }}
            >
              <p className="text-[10px] font-semibold text-gray-600 tracking-widest uppercase mb-3">
                On this page
              </p>

              {sortedCases.length > 0 && (
                <a
                  href="#repairs"
                  onClick={e => { e.preventDefault(); scrollToRef('repairs'); }}
                  className="flex items-center justify-between text-gray-400 hover:text-[#E0E0E0] py-1.5 px-2 rounded-lg hover:bg-gray-900 transition-colors text-sm mb-1"
                >
                  <span>Repair Guides</span>
                  <span className="text-gray-600 text-xs">{sortedCases.length}</span>
                </a>
              )}

              {searchedRefs.length > 0 && (
                <>
                  <p className="text-[10px] font-semibold text-gray-600 tracking-widest uppercase mt-4 mb-2 px-2">
                    References
                  </p>
                  <div className="space-y-0.5">
                    {searchedRefs.map(r => (
                      <a
                        key={r.id}
                        href={`#ref-${r.id}`}
                        onClick={e => { e.preventDefault(); scrollToRef(`ref-${r.id}`); }}
                        className={`block py-1.5 px-2 rounded-lg text-sm transition-colors leading-snug ${
                          activeRefId === r.id
                            ? 'text-[#00BFFF] bg-[#00BFFF]/8'
                            : 'text-gray-400 hover:text-[#E0E0E0] hover:bg-gray-900'
                        }`}
                      >
                        {r.title}
                      </a>
                    ))}
                  </div>
                </>
              )}

              <div className="pt-4 mt-4 border-t border-gray-800/60 space-y-0.5">
                <Link
                  to="/repair-database"
                  className="block text-gray-500 hover:text-[#E0E0E0] py-1.5 px-2 rounded-lg hover:bg-gray-900 transition-colors text-xs"
                >
                  ← Device List
                </Link>
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="block w-full text-left text-gray-500 hover:text-[#E0E0E0] py-1.5 px-2 rounded-lg hover:bg-gray-900 transition-colors text-xs"
                >
                  ↑ Back to Top
                </button>
              </div>
            </div>
          </aside>

          {/* ── Main content ── */}
          <div className="min-w-0 flex-1">

            {/* Page title */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <Wrench className="h-5 w-5 text-[#FF1744]" />
                {mfr && <span className="text-sm text-gray-500">{mfr.name}</span>}
              </div>
              <h1 className="text-3xl md:text-4xl font-black text-white mb-2">{family.name}</h1>
              <p className="text-gray-500 text-sm">
                {cases.length > 0 && `${cases.length} repair case${cases.length !== 1 ? 's' : ''}`}
                {cases.length > 0 && refs.length > 0 && ' · '}
                {refs.length > 0 && `${refs.length} reference${refs.length !== 1 ? 's' : ''}`}
              </p>
            </div>

            {/* Controls: Search + Board Revision */}
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search repairs, board revisions, error codes or components…"
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl pl-9 pr-8 py-2.5 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {boardsWithContent.length > 0 && (
                <div className="relative flex-shrink-0">
                  <select
                    value={boardId}
                    onChange={e => { setBoardId(e.target.value); setRefTypeFilter('all'); setActiveRefId(null); }}
                    className="appearance-none bg-gray-900 border border-gray-700 rounded-xl pl-3 pr-8 py-2.5 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#FF1744]/60 cursor-pointer w-full sm:w-auto"
                  >
                    <option value="all">All Revisions</option>
                    {boardsWithContent.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                </div>
              )}
            </div>

            {/* Mobile "Jump to Reference" dropdown */}
            {searchedRefs.length > 0 && (
              <div className="lg:hidden mb-6 relative">
                <button
                  onClick={() => setMobileNavOpen(o => !o)}
                  className="flex items-center justify-between w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-[#E0E0E0]"
                >
                  <span className="flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-[#00BFFF]" />
                    Jump to Reference
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${mobileNavOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileNavOpen && (
                  <div className="absolute top-full left-0 right-0 z-20 bg-gray-900 border border-gray-700 rounded-xl mt-1 shadow-xl max-h-64 overflow-y-auto">
                    {searchedRefs.map((r, i) => (
                      <button
                        key={r.id}
                        onClick={() => {
                          scrollToRef(`ref-${r.id}`);
                          setMobileNavOpen(false);
                        }}
                        className={`block w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800/60 transition-colors ${
                          i < searchedRefs.length - 1 ? 'border-b border-gray-800/60' : ''
                        }`}
                      >
                        {r.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Top banner ad */}
            {adConfig.enabled && (
              <AdBanner
                publisherId={adConfig.publisher_id}
                slotId={adConfig.placements.banner_slot_id}
                enabled={adConfig.placements.banner_enabled}
              />
            )}

            {/* ══ SECTION 1: Repair Guides ══ */}
            <section id="repairs" className="scroll-mt-24 mb-14">
              <h2 className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-4 flex items-center gap-2">
                <Wrench className="h-3 w-3" />
                Repair Guides
                {sortedCases.length > 0 && (
                  <span className="text-gray-600 font-normal normal-case tracking-normal">
                    ({sortedCases.length})
                  </span>
                )}
              </h2>

              {sortedCases.length === 0 ? (
                <div className="text-center py-10 text-gray-600 border border-gray-800 rounded-xl">
                  <p className="text-sm">
                    {q ? 'No repairs match this search.' : 'No published repair guides yet.'}
                  </p>
                </div>
              ) : (
                <div className="border border-gray-800 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800 bg-gray-900/60">
                        <th className="text-left px-4 py-3 font-semibold text-gray-400">
                          <SortBtn label="Repair Title"   col="title" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-400 w-40">
                          <SortBtn label="Board Revision" col="board" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCases.map((c, i) => {
                        const boardName = allBoards.find(b => b.id === c.board_revision_id)?.name;
                        return (
                          <tr
                            key={c.id}
                            className={`hover:bg-gray-900/40 transition-colors ${
                              i < sortedCases.length - 1 ? 'border-b border-gray-800/60' : ''
                            }`}
                          >
                            <td className="px-4 py-3">
                              <Link
                                to={caseUrl(c)}
                                className="text-[#E0E0E0] hover:text-white font-medium hover:underline underline-offset-2"
                              >
                                {c.title || `${family.name} Repair`}
                              </Link>
                            </td>
                            <td className="px-4 py-3">
                              {boardName ? (
                                <span className="text-xs font-mono text-gray-500 bg-gray-800/60 px-2 py-1 rounded">
                                  {boardName}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-700">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* In-content ad between sections */}
            {adConfig.enabled && (
              <AdInContent
                publisherId={adConfig.publisher_id}
                slotId={adConfig.placements.in_content_slot_id}
                enabled={adConfig.placements.in_content_enabled}
              />
            )}

            {/* ══ SECTION 2: Known-Good Measurements ══ */}
            <section id="references" className="scroll-mt-24 mb-10">
              <h2 className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-4 flex items-center gap-2">
                <Cpu className="h-3 w-3" />
                Known-Good Measurements &amp; Board References
                {searchedRefs.length > 0 && (
                  <span className="text-gray-600 font-normal normal-case tracking-normal">
                    ({searchedRefs.length})
                  </span>
                )}
              </h2>

              {/* Measurement type filter */}
              {presentRefTypes.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  <button
                    onClick={() => setRefTypeFilter('all')}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                      refTypeFilter === 'all'
                        ? 'bg-[#00BFFF]/15 border-[#00BFFF]/50 text-[#00BFFF]'
                        : 'border-gray-700 text-gray-500 hover:text-[#E0E0E0] hover:border-gray-500'
                    }`}
                  >
                    All ({refsForBoard.length})
                  </button>
                  {presentRefTypes.map(t => {
                    const count = refsForBoard.filter(r => r.measurement_type === t.value).length;
                    return (
                      <button
                        key={t.value}
                        onClick={() => setRefTypeFilter(t.value)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                          refTypeFilter === t.value
                            ? 'bg-[#00BFFF]/15 border-[#00BFFF]/50 text-[#00BFFF]'
                            : 'border-gray-700 text-gray-500 hover:text-[#E0E0E0] hover:border-gray-500'
                        }`}
                      >
                        {t.shortLabel} ({count})
                      </button>
                    );
                  })}
                </div>
              )}

              {searchedRefs.length === 0 ? (
                <div className="text-center py-10 text-gray-600 border border-gray-800 rounded-xl">
                  <FileText className="h-8 w-8 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">
                    {q ? 'No references match this search.' :
                     refTypeFilter !== 'all' ? 'No references match this filter.' :
                     boardId !== 'all' ? `No references for ${selectedBoard?.name ?? 'this board'} yet.` :
                     'No published references yet.'}
                  </p>
                  {refTypeFilter !== 'all' && (
                    <button
                      onClick={() => setRefTypeFilter('all')}
                      className="mt-2 text-xs text-[#00BFFF] hover:underline"
                    >
                      Clear filter
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-12">
                  {searchedRefs.map(r => {
                    const media     = r.repair_reference_media ?? [];
                    const typeInfo  = REF_MEASUREMENT_TYPES.find(t => t.value === r.measurement_type);
                    const boardName = allBoards.find(b => b.id === r.board_revision_id)?.name;
                    return (
                      <RefArticle
                        key={r.id}
                        r={r}
                        media={media}
                        typeInfo={typeInfo}
                        boardName={boardName}
                        isActive={activeRefId === r.id}
                        onActivate={setActiveRefId}
                        onOpenLightbox={(photoIdx) => setLightbox({ refId: r.id, photoIdx })}
                      />
                    );
                  })}
                </div>
              )}
            </section>

            {/* Empty state */}
            {sortedCases.length === 0 && searchedRefs.length === 0 && !q && (
              <div className="text-center py-20 text-gray-600">
                <Cpu className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>
                  No published content{selectedBoard ? ` for ${selectedBoard.name}` : ` for ${family.name}`} yet.
                </p>
              </div>
            )}

            {/* CTA */}
            <div className="border-t border-gray-800 pt-10 mt-4 text-center">
              <p className="text-gray-400 mb-2 text-sm">Have a {family.name} that needs repair?</p>
              <p className="text-gray-500 text-xs mb-5">
                GameFix Solutions accepts mail-in repairs nationwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/book-repair"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF1744] hover:bg-[#D50000] text-white font-bold px-5 py-2.5 rounded-xl transition-colors text-sm"
                >
                  Book a Repair <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/mail-in-repairs"
                  className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold px-5 py-2.5 rounded-xl border border-gray-700 transition-colors text-sm"
                >
                  Mail-In Repair
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Reference article with IntersectionObserver for active tracking ───────────

function RefArticle({
  r, media, typeInfo, boardName, isActive, onActivate, onOpenLightbox,
}: {
  r:             RepairReference;
  media:         RefMedia[];
  typeInfo:      { value: string; label: string; shortLabel: string } | undefined;
  boardName:     string | undefined;
  isActive:      boolean;
  onActivate:    (id: string) => void;
  onOpenLightbox:(photoIdx: number) => void;
}) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onActivate(r.id);
      },
      // Trigger when the top of the article enters the upper quarter of the viewport
      { rootMargin: '-80px 0px -65% 0px', threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [r.id, onActivate]);

  return (
    <article
      ref={ref}
      id={`ref-${r.id}`}
      className="scroll-mt-24"
    >
      {/* Reference title row */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-2.5 flex-wrap min-w-0">
          <h3 className="text-lg font-bold text-white leading-snug">{r.title}</h3>
          {typeInfo && (
            <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-[#00BFFF]/15 border border-[#00BFFF]/30 text-[#00BFFF] mt-0.5 flex-shrink-0">
              {typeInfo.shortLabel}
            </span>
          )}
          {boardName && (
            <span className="text-xs font-mono text-gray-500 bg-gray-800/60 px-2 py-0.5 rounded mt-0.5 flex-shrink-0">
              {boardName}
            </span>
          )}
        </div>
        <Link
          to={`/repair-database/ref/${r.slug || r.id}`}
          className="text-xs text-gray-500 hover:text-[#00BFFF] whitespace-nowrap flex-shrink-0 transition-colors"
        >
          Full reference →
        </Link>
      </div>

      {/* Photos — full-width vertical stack */}
      {media.length === 0 ? (
        <div className="w-full h-48 bg-gray-900 rounded-xl flex items-center justify-center border border-gray-800">
          <FileText className="h-8 w-8 text-gray-700" />
        </div>
      ) : (
        <div className="space-y-3">
          {media.map((m, photoIdx) => (
            <div key={m.id}>
              <img
                src={m.public_url}
                alt={m.caption || r.title}
                className="w-full rounded-xl cursor-zoom-in bg-gray-950"
                style={{ objectFit: 'contain' }}
                onClick={() => onOpenLightbox(photoIdx)}
              />
              {m.caption && (
                <p className="text-xs text-gray-500 mt-2 px-1">{m.caption}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {r.notes && (
        <p className="text-sm text-gray-400 mt-4 leading-relaxed">{r.notes}</p>
      )}
    </article>
  );
}
